// define the shape of your environment variables
interface Environment {
    version: string;
    backendUrl: string;
    registrationEnabled: boolean;
    emailLoginRequired: boolean;
    hasExternalPasswordReset: boolean;
    passwordResetUrl: string;
    clientTheme: string;
}

export enum Brand {
    default = 'default',
    uck= 'uck',
}

const CLIENT_THEMES: Brand[] = [Brand.default, Brand.uck];

export type ClientTheme = (typeof CLIENT_THEMES)[number];

export function isClientTheme(value: string): value is ClientTheme {
    return (CLIENT_THEMES as readonly string[]).includes(value);
}

const ALLOWED_DOMAINS = ['uck.gda.pl'];

function isSafeUrl(url: string): boolean {
    try {
        const parsed = new URL(url);
        return parsed.protocol === 'https:' && ALLOWED_DOMAINS.includes(parsed.hostname);
    } catch {
        return false;
    }
}

// type declaration for window.__RUNTIME_CONFIG__ - set by
// runtime-config.js, generated by Docker during the container's start
declare global {
    interface Window {
        __RUNTIME_CONFIG__?: {
            REACT_APP_AI_ASSISTANT_BACKEND_URL?: string;
            REACT_APP_REGISTRATION_ENABLED?: string;
            REACT_APP_EMAIL_LOGIN_REQUIRED?: string;
            REACT_APP_HAS_EXTERNAL_PASSWORD_RESET?: string;
            REACT_APP_PASSWORD_RESET_URL?: string;
            REACT_APP_CLIENT_THEME?: string;
        };
    }
}

// parse and validate environment variables

// priority: window.__RUNTIME_CONFIG__ (Docker / production)
// fallback: process.env (npm start - local development in .env.local without Docker)
function getEnvironment(): Environment {
    const runtimeConfig = window.__RUNTIME_CONFIG__ || {};

    const backendUrl =
        (!!runtimeConfig.REACT_APP_AI_ASSISTANT_BACKEND_URL && runtimeConfig.REACT_APP_AI_ASSISTANT_BACKEND_URL?.length > 0)
            ? runtimeConfig.REACT_APP_AI_ASSISTANT_BACKEND_URL
            : process.env.REACT_APP_AI_ASSISTANT_BACKEND_URL;

    const registrationEnabled =
        (!!runtimeConfig.REACT_APP_REGISTRATION_ENABLED && runtimeConfig.REACT_APP_REGISTRATION_ENABLED.length > 0)
            ? runtimeConfig.REACT_APP_REGISTRATION_ENABLED
            : process.env.REACT_APP_REGISTRATION_ENABLED;

    const emailLoginRequired = (!!runtimeConfig.REACT_APP_EMAIL_LOGIN_REQUIRED && runtimeConfig.REACT_APP_EMAIL_LOGIN_REQUIRED.length > 0)
        ? runtimeConfig.REACT_APP_EMAIL_LOGIN_REQUIRED
        : process.env.REACT_APP_EMAIL_LOGIN_REQUIRED;

    const hasExternalPasswordReset = (!!runtimeConfig.REACT_APP_HAS_EXTERNAL_PASSWORD_RESET && runtimeConfig.REACT_APP_HAS_EXTERNAL_PASSWORD_RESET.length > 0)
        ? runtimeConfig.REACT_APP_HAS_EXTERNAL_PASSWORD_RESET
        : process.env.REACT_APP_HAS_EXTERNAL_PASSWORD_RESET;

    const rawPasswordResetUrl = (!!runtimeConfig.REACT_APP_PASSWORD_RESET_URL && runtimeConfig.REACT_APP_PASSWORD_RESET_URL.length > 0)
        ? runtimeConfig.REACT_APP_PASSWORD_RESET_URL
        : process.env.REACT_APP_PASSWORD_RESET_URL;

    const passwordResetUrl = (rawPasswordResetUrl && rawPasswordResetUrl?.length > 0 && isSafeUrl(rawPasswordResetUrl))
        ? rawPasswordResetUrl
        : '';

    const rawClientTheme =
        (!!runtimeConfig.REACT_APP_CLIENT_THEME && runtimeConfig.REACT_APP_CLIENT_THEME?.length > 0)
            ? runtimeConfig.REACT_APP_CLIENT_THEME
            : process.env.REACT_APP_CLIENT_THEME;

    const clientTheme = isClientTheme(rawClientTheme ?? '') ? rawClientTheme : 'default';

    return {
        version: process.env.REACT_APP_VERSION || '0.0.0',
        backendUrl: backendUrl || '',
        registrationEnabled: registrationEnabled !== 'false',   // default: true
        emailLoginRequired: emailLoginRequired !== 'false',     // default: true
        hasExternalPasswordReset: hasExternalPasswordReset === 'true' && passwordResetUrl?.length > 0, // default: false
        passwordResetUrl: passwordResetUrl || '',               // default: empty string
        clientTheme: clientTheme || 'default',
    };
}

export const env = getEnvironment();